import React, { useState, useCallback, useEffect } from 'react';
import {
  Calendar,
  momentLocalizer,
  View,
  SlotInfo,
  NavigateAction,
  DateLocalizer,
} from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import EventDetailsDialog from 'src/components/BigCalender/EventDetailsDialogue';
import { getEventIcon } from 'src/utils/BigCalendarColourGeneratorUtils';
import { getEventStyle } from 'src/components/BigCalender/helpers';
import CustomToolbar from 'src/components/CustomToolbar/CustomToolbar';
import { ICalendarEvent } from 'src/pages/Calendar/types';
import './big-calender.scss';

// We want to configure moment to start weeks on Monday instead of Sunday.
moment.updateLocale('en', {
  week: {
    dow: 1, // Monday is the first day of the week (0 = Sunday, 1 = Monday).
    doy: 3, // With weeks starting on Monday, the 3rd would be the first week.
  },
});

// Set up the localizer by providing the moment Object.
const localizer: DateLocalizer = momentLocalizer(moment);

const EmptyCalendarState = () => {
  return (
    <div className="empty-calendar-state">
      <div className="empty-state-content">
        <div className="empty-state-icon">📅</div>
        <h3 className="empty-state-title">No events in this date range</h3>
        <p className="empty-state-description">
          Try widening your date filters above to see more events, or check back
          later for new bookings.
        </p>
      </div>
    </div>
  );
};

interface EventProps {
  event: ICalendarEvent;
}

const CustomEvent = ({ event }: EventProps) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        padding: '2px 4px',
      }}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}
      >
        {event.title}
      </span>
      <span
        style={{
          marginLeft: '4px',
          flexShrink: 0,
          color: 'inherit',
        }}
      >
        {getEventIcon(event.eventType)}
      </span>
    </div>
  );
};

interface IBigCalendarProps {
  events?: ICalendarEvent[];
  onSelectEvent?: (event: ICalendarEvent) => void;
  onSelectSlot?: (slotInfo: SlotInfo) => void;
  onNavigate?: (date: Date, view: View, action: NavigateAction) => void;
  defaultDate?: Date;
  selectable?: boolean;
  popup?: boolean;
  style?: React.CSSProperties;
  minDate?: Date;
  maxDate?: Date;
  allItemsNothighlighted?: boolean;
}

const BigCalendar = ({
  events = [],
  onSelectEvent,
  onSelectSlot,
  onNavigate,
  defaultDate = new Date(),
  selectable = true,
  popup = true,
  style,
  minDate,
  maxDate,
  allItemsNothighlighted,
}: IBigCalendarProps) => {
  const [date, setDate] = useState<Date>(defaultDate);
  const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent | null>(
    null
  );
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  // Ensure initial date is within bounds.
  useEffect(() => {
    if (minDate && date < minDate) {
      setDate(new Date(minDate));
    } else if (maxDate && date > maxDate) {
      setDate(new Date(maxDate));
    }
  }, [minDate, maxDate, date]);

  // Custom navigation handler that enforces min/max bounds.
  const handleNavigate = useCallback(
    (newDate: Date, view: View, action: NavigateAction) => {
      let navigateDate = new Date(newDate);

      if (minDate && navigateDate < minDate) {
        navigateDate = new Date(minDate);
      } else if (maxDate && navigateDate > maxDate) {
        navigateDate = new Date(maxDate);
      }

      setDate(navigateDate);

      if (onNavigate) {
        onNavigate(navigateDate, view, action);
      }
    },
    [minDate, maxDate, onNavigate]
  );

  // Enhanced default event handler that opens the dialog.
  const handleSelectEvent = useCallback(
    (event: ICalendarEvent) => {
      setSelectedEvent(event);
      setIsDialogOpen(true);

      // If parent provided an onSelectEvent handler, call it as well.
      if (onSelectEvent) {
        onSelectEvent(event);
      }
    },
    [onSelectEvent]
  );

  // Render the custom toolbar with access to minDate and maxDate.
  const renderToolbar = useCallback(
    (toolbarProps: any) => (
      <CustomToolbar {...toolbarProps} minDate={minDate} maxDate={maxDate} />
    ),
    [minDate, maxDate]
  );

  if (allItemsNothighlighted) {
    return <EmptyCalendarState />;
  }

  return (
    <>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        view="month"
        views={['month']}
        date={date}
        onNavigate={handleNavigate}
        selectable={selectable}
        onSelectEvent={handleSelectEvent}
        eventPropGetter={getEventStyle}
        popup={popup}
        showMultiDayTimes={false}
        min={minDate}
        max={maxDate}
        components={{
          toolbar: renderToolbar,
          event: CustomEvent,
        }}
        style={{
          height: '90%',
          width: '90%',
          ...style,
        }}
      />
      <EventDetailsDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        event={selectedEvent}
      />
    </>
  );
};

export default BigCalendar;
